import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export default function LiveControlPanel() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [discipline, setDiscipline] = useState("all");

  const load = useCallback(async () => {
    const [mr, tr] = await Promise.all([api.get("/matches"), api.get("/admin/teams")]);
    setMatches(mr.data);
    setTeams(tr.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const name = (id) => teams.find((t) => t.id === id)?.name || "—";

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/matches/${id}`, body);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = matches.filter((m) => discipline === "all" || m.discipline === discipline);
  const live = filtered.filter((m) => m.status === "live");
  const upcoming = filtered.filter((m) => m.status === "upcoming");
  const finished = filtered.filter((m) => m.status === "finished");

  return (
    <div className="space-y-8" data-testid="live-control-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-white/50">
          Régie de l'écran live — les scores s'affichent sur /live en moins de 5 s
        </span>
        <select
          className="uf-input !py-2 !px-3 text-xs ml-auto"
          value={discipline}
          onChange={(e) => setDiscipline(e.target.value)}
          data-testid="regie-discipline-select"
        >
          <option value="all">Toutes disciplines</option>
          <option value="basket">Basket 3x3</option>
          <option value="foot">Foot 5v5</option>
        </select>
        <a href="/live" target="_blank" rel="noopener noreferrer" className="btn-brutal !py-2 !px-4 !text-xs">
          📺 Voir l'écran
        </a>
      </div>

      <section>
        <h3 className="font-display text-2xl mb-4 text-[#FF3B30]">● EN DIRECT ({live.length})</h3>
        {live.length === 0 ? (
          <p className="text-white/40 text-sm">Aucun match en cours. Lancez un match ci-dessous.</p>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {live.map((m) => (
              <div key={m.id} className="uf-card border-2 border-[#FF3B30]/60 p-5" data-testid={`regie-live-${m.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#FFB800]">
                    {m.discipline === "basket" ? "🏀 Basket" : "⚽ Foot"} · {m.phase}{m.pool ? ` · Poule ${m.pool}` : ""}
                  </span>
                  <button
                    onClick={() => patch(m.id, { status: "finished" })}
                    className="text-xs uppercase tracking-widest font-bold bg-[#2EE59D] text-black px-3 py-1.5 rounded-full hover:brightness-110"
                    data-testid={`regie-finish-${m.id}`}
                  >
                    ✓ Terminer
                  </button>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  <ScoreSide m={m} side="a" label={name(m.team_a_id)} patch={patch} />
                  <span className="font-display text-3xl text-white/30">–</span>
                  <ScoreSide m={m} side="b" label={name(m.team_b_id)} patch={patch} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-display text-2xl mb-4 text-[#FFB800]">À VENIR ({upcoming.length})</h3>
        <div className="uf-card divide-y divide-white/5">
          {upcoming.length === 0 ? (
            <p className="p-5 text-white/40 text-sm">Aucun match en attente.</p>
          ) : upcoming.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-3" data-testid={`regie-upcoming-${m.id}`}>
              <span className="text-[10px] font-black uppercase tracking-widest text-white/40 w-28 shrink-0">
                {m.discipline === "basket" ? "Basket" : "Foot"} · {m.phase}{m.pool ? ` ${m.pool}` : ""}
              </span>
              <span className="flex-1 font-bold truncate">{name(m.team_a_id)} <span className="text-white/40">vs</span> {name(m.team_b_id)}</span>
              <button
                onClick={() => patch(m.id, { status: "live" })}
                className="text-xs uppercase tracking-widest font-bold bg-[#FF3B30] text-white px-4 py-2 rounded-full hover:brightness-110"
                data-testid={`regie-start-${m.id}`}
              >
                ▶ Lancer
              </button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-display text-2xl mb-4 text-[#2EE59D]">TERMINÉS ({finished.length})</h3>
        <div className="uf-card divide-y divide-white/5">
          {finished.length === 0 ? (
            <p className="p-5 text-white/40 text-sm">Aucun match terminé.</p>
          ) : finished.slice().reverse().map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-3" data-testid={`regie-finished-${m.id}`}>
              <span className="text-[10px] font-black uppercase tracking-widest text-white/40 w-28 shrink-0">
                {m.discipline === "basket" ? "Basket" : "Foot"} · {m.phase}{m.pool ? ` ${m.pool}` : ""}
              </span>
              <span className="flex-1 font-bold truncate">
                {name(m.team_a_id)} <span className="font-display text-lg text-[#2EE59D] px-1">{m.score_a} - {m.score_b}</span> {name(m.team_b_id)}
              </span>
              <button
                onClick={() => patch(m.id, { status: "live" })}
                className="text-xs uppercase tracking-widest border border-white/20 text-white/60 px-3 py-1.5 rounded-full hover:text-white"
                data-testid={`regie-reopen-${m.id}`}
              >
                Rouvrir
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ScoreSide({ m, side, label, patch }) {
  const key = `score_${side}`;
  const val = m[key];
  const set = (delta) => patch(m.id, { [key]: Math.max(0, val + delta) });
  return (
    <div className="text-center min-w-0">
      <p className="font-bold truncate mb-1">{label}</p>
      <p className="font-display text-5xl text-[#FF6B1A] mb-2" data-testid={`regie-score-${side}-${m.id}`}>{val}</p>
      <div className="flex justify-center gap-1.5 flex-wrap">
        {[1, 2, 3].map((d) => (
          <button
            key={d}
            onClick={() => set(d)}
            className="w-9 h-9 rounded-full bg-[#FF6B1A] text-black font-black text-sm hover:brightness-110"
            data-testid={`regie-plus${d}-${side}-${m.id}`}
          >
            +{d}
          </button>
        ))}
        <button
          onClick={() => set(-1)}
          className="w-9 h-9 rounded-full border border-white/25 text-white/60 font-black text-sm hover:text-white"
          data-testid={`regie-minus-${side}-${m.id}`}
        >
          −1
        </button>
      </div>
    </div>
  );
}
